import { useState, useRef, useEffect, useMemo } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Mic, Square, RotateCcw, Save, Loader2, X } from "lucide-react";

const pickMime = () => {
  const list = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
  return list.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
};
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

function useRecorder() {
  const [state, setState] = useState("idle");
  const [blob, setBlob] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const rec = useRef(null);
  const timer = useRef(null);

  const stopTracks = () => rec.current?.stream?.getTracks().forEach((t) => t.stop());
  useEffect(() => () => { clearInterval(timer.current); stopTracks(); }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      r.onstop = () => { setBlob(new Blob(chunks, { type: r.mimeType || mime || "audio/webm" })); setState("done"); stopTracks(); };
      rec.current = r;
      r.start(1000);
      setSeconds(0); setBlob(null); setState("recording");
      timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch { toast.error("Le micro n'est pas accessible. Autorisez-le dans votre navigateur."); }
  };
  const stop = () => { clearInterval(timer.current); rec.current?.stop(); };
  const reset = () => { setBlob(null); setSeconds(0); setState("idle"); };
  return { state, blob, seconds, start, stop, reset };
}

export default function VoiceRecorder({ postUrl, fields = {}, askTitle, hideName, title = "Lire ce conte de votre voix", intro = "Lisez le texte ci-dessous tranquillement, à votre rythme. Vous pourrez réécouter avant d'enregistrer.", defaultName, onSaved, onClose }) {
  const { state, blob, seconds, start, stop, reset } = useRecorder();
  const [name, setName] = useState(defaultName || "");
  const [saving, setSaving] = useState(false);
  const [songTitle, setSongTitle] = useState("");
  const preview = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);

  const save = async () => {
    if (!hideName && !name.trim()) return toast.error("Indiquez votre prénom.");
    if (askTitle && !songTitle.trim()) return toast.error("Indiquez le titre de la chanson.");
    setSaving(true);
    try {
      const fd = new FormData();
      const ext = blob.type.includes("mp4") ? "m4a" : blob.type.includes("ogg") ? "ogg" : "webm";
      Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
      if (askTitle) fd.append("song_title", songTitle.trim());
      fd.append("label", name.trim());
      fd.append("file", blob, `voix.${ext}`);
      const r = await api.post(postUrl, fd);
      toast.success("Votre enregistrement est sauvegardé.");
      onSaved(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "L'enregistrement n'a pas pu être envoyé."); }
    finally { setSaving(false); }
  };

  const pill = "inline-flex items-center gap-2 rounded-full px-6 h-12 font-patient font-bold text-lg active:scale-95 transition-transform disabled:opacity-60";
  return (
    <div className="rounded-3xl bg-warm/10 border border-warm/30 p-5 sm:p-6 my-6 relative soft-in" data-testid="voice-recorder">
      <button onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-full hover:bg-white flex items-center justify-center" aria-label="Fermer" data-testid="voice-recorder-close">
        <X className="w-5 h-5" />
      </button>
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{title}</p>
      <p className="text-foreground/75 mt-1 mb-4">{intro}</p>
      {!hideName && (
        <>
          <label className="block text-sm font-semibold mb-1" htmlFor="rec-name">Votre prénom (affiché au résident)</label>
          <input id="rec-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} data-testid="voice-recorder-name"
            className="w-full max-w-xs h-11 rounded-full border border-border bg-white px-4 mb-5" placeholder="Ex. Marie" />
        </>
      )}
      {askTitle && (
        <>
          <label className="block text-sm font-semibold mb-1" htmlFor="rec-song">Titre de la chanson</label>
          <input id="rec-song" value={songTitle} onChange={(e) => setSongTitle(e.target.value)} maxLength={80} data-testid="voice-recorder-song-title"
            className="w-full max-w-sm h-11 rounded-full border border-border bg-white px-4 mb-5" placeholder="Ex. La Vie en rose" />
        </>
      )}
      <div className="flex flex-wrap items-center gap-3">
        {state === "idle" && (
          <button onClick={start} className={`${pill} bg-rose-500 text-white`} data-testid="voice-recorder-start"><Mic className="w-5 h-5" /> Commencer</button>
        )}
        {state === "recording" && (
          <>
            <span className="inline-flex items-center gap-2 font-patient font-bold text-rose-600" data-testid="voice-recorder-timer">
              <span className="w-3 h-3 rounded-full bg-rose-500 soft-pulse" /> {fmt(seconds)}
            </span>
            <button onClick={stop} className={`${pill} bg-gray-800 text-white`} data-testid="voice-recorder-stop"><Square className="w-5 h-5" /> Arrêter</button>
          </>
        )}
        {state === "done" && preview && (
          <>
            <audio src={preview} controls className="h-12" data-testid="voice-recorder-preview" />
            <button onClick={reset} className={`${pill} bg-white border-2 border-gray-200 text-gray-700`} data-testid="voice-recorder-retry"><RotateCcw className="w-5 h-5" /> Recommencer</button>
            <button onClick={save} disabled={saving} className={`${pill} bg-emerald-700 text-white`} data-testid="voice-recorder-save">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Enregistrer
            </button>
          </>
        )}
      </div>
    </div>
  );
}
